import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpDown,
  Download,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { Permission, useCan } from "@/components/session-provider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  type AppUsageMonthPoint,
  type AppUsagePerson,
  type AppUsageQuery,
  type AppUsageSegment,
  type AppUsageSitePoint,
  type AppUsageSortField,
  type AppUsageSummary,
  type DormancyWindow,
  exportAppUsage,
  fetchAppUsage,
  fetchAppUsagePeople,
  formatSignInDate,
  formatYearMonth,
  MinimumJustificationLength,
  SegmentDescriptions,
  SegmentTitles,
  UnsitedToken,
} from "@/lib/reports-app-usage";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/app-usage")({
  component: AppUsagePage,
});

const SEGMENTS: readonly AppUsageSegment[] = ["NeverSignedIn", "Dormant", "DormantAdministrators"];

const WINDOWS: readonly { value: DormancyWindow; label: string }[] = [
  { value: "Days30", label: "30 days" },
  { value: "Days60", label: "60 days" },
  { value: "Days90", label: "90 days" },
  { value: "Days180", label: "180 days" },
];

/**
 * **App adoption.**
 *
 * Three questions in the order people ask them: how many are using the service desk this month
 * against last, who has never signed in at all, and which administrative accounts have gone quiet.
 *
 * ⚠️ **Gated on `user.manage`, not on a reporting tier**, and the server is what enforces it. The
 * rows are roster rows carrying names, emails, reporting lines and the day each account was last
 * used — so the roster's fence applies (D-80). The reporting tiers describe reach over requests.
 *
 * ⚠️ **It counts accounts and sign-ins, never requests.** The obvious third step of an activation
 * funnel — "…and then raised a request" — is deliberately absent: the request table is scoped to
 * the caller in both enforcement layers, so a count taken here would mean "requests I am allowed
 * to see" and two administrators would read different answers to "has this colleague ever used
 * the desk". That question lives on the request-activity report, where the scoping is the point.
 */
function AppUsagePage() {
  const canOpenProfiles = useCan(Permission.UserManage);

  const [query, setQuery] = useState<AppUsageQuery>({
    segment: "NeverSignedIn",
    window: "Days90",
    sortBy: "Name",
    direction: "Ascending",
  });

  /**
   * What is typed, as distinct from what has been asked for.
   *
   * ⚠️ **The search runs on SUBMIT, never per keystroke** — D-33 and D-78's rule. Every load of a
   * worklist writes an audit event, so a live search files one for `h`, `hi` and `hid` on the way
   * to `hidalgo`, and burns the search rate limit doing it. A debounce narrows that window; it
   * does not close it.
   */
  const [typed, setTyped] = useState("");

  const [summary, setSummary] = useState<AppUsageSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [rows, setRows] = useState<AppUsagePerson[]>([]);
  const [totalMatching, setTotalMatching] = useState<number | null>(null);
  const [totalInSegment, setTotalInSegment] = useState<number | null>(null);
  const [totalAccounts, setTotalAccounts] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Every filter is part of the fetch key. Serialised rather than listed so adding one cannot be
  // forgotten here and silently stop refetching.
  const key = useMemo(() => JSON.stringify(query), [query]);

  /**
   * ⚠️ The summary refetches only when something that CHANGES IT changes.
   *
   * The segment, sort and search narrow the worklist and leave every headline figure alone, so
   * refetching on those would write an extra audit event per tab click into an append-only table
   * — D-79's defect, where live filtering wrote one row per keystroke into a ledger that can
   * never be corrected. The window and the deactivated toggle genuinely move the figures.
   */
  const summaryKey = useMemo(
    () => JSON.stringify({ window: query.window, includeInactive: query.includeInactive }),
    [query.window, query.includeInactive],
  );

  useEffect(() => {
    const controller = new AbortController();

    setSummaryError(null);

    fetchAppUsage({ window: query.window, includeInactive: query.includeInactive })
      .then((next) => {
        if (!controller.signal.aborted) setSummary(next);
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setSummaryError(
          cause instanceof Error ? cause.message : "Those figures could not be loaded.",
        );
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey]);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetchAppUsagePeople(query)
      .then((page) => {
        if (controller.signal.aborted) return;

        setRows(page.items);
        setTotalMatching(page.totalMatching);
        setTotalInSegment(page.totalInSegment);
        setTotalAccounts(page.totalAccounts);
        setCursor(page.nextCursor);
      })
      .catch((cause) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "That list could not be loaded.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);

    fetchAppUsagePeople(query, cursor)
      .then((page) => {
        setRows((existing) => [...existing, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch(() => setError("The next page could not be loaded."))
      .finally(() => setLoadingMore(false));
  }, [cursor, query, loadingMore]);

  /**
   * ⚠️ Clicking the sorted column flips the direction; clicking another starts it ascending. The
   * cursor is not carried over — the server discards one made under a different sort, because it
   * describes a position in an ordering that no longer applies.
   */
  const sortBy = (field: AppUsageSortField) => {
    setQuery((q) => ({
      ...q,
      sortBy: field,
      direction: q.sortBy === field && q.direction === "Ascending" ? "Descending" : "Ascending",
    }));
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery((q) => ({ ...q, search: typed.trim() || undefined }));
  };

  const filtered = query.search !== undefined || query.siteId !== undefined;

  const clearFilters = () => {
    setTyped("");
    setQuery((q) => ({
      segment: q.segment,
      window: q.window,
      sortBy: q.sortBy,
      direction: q.direction,
      includeInactive: q.includeInactive,
    }));
  };

  const runExport = async (justification: string) => {
    setExporting(true);
    setError(null);

    try {
      await exportAppUsage(query, justification);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That export could not be produced.");
      throw cause;
    } finally {
      setExporting(false);
    }
  };

  const segment = query.segment ?? "NeverSignedIn";

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 sm:p-6">
      <header className="space-y-3">
        <Link
          to="/reports"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Reports
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">App adoption</h1>
            <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
              Who has an account, how many of them use it, and whose access nobody is behind.
              Figures are counts of accounts and sign-ins — this report does not count requests.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={query.includeInactive ?? false}
                onChange={(event) =>
                  setQuery((q) => ({ ...q, includeInactive: event.target.checked || undefined }))
                }
              />
              Include deactivated
            </label>

            <ExportButton
              disabled={loading || (totalMatching ?? 0) === 0}
              busy={exporting}
              rowCount={totalMatching ?? 0}
              segment={segment}
              filtered={filtered}
              onExport={runExport}
            />
          </div>
        </div>
      </header>

      {summaryError && (
        <p className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          {summaryError}
        </p>
      )}

      {summary && (
        <>
          <MonthlyCard summary={summary} />
          <AdoptionTiles summary={summary} />
          <AdministratorCard
            summary={summary}
            onOpen={() => setQuery((q) => ({ ...q, segment: "DormantAdministrators" }))}
          />
          <Trend points={summary.trend} coverage={summary.monthly.coverageFromYearMonth} />
          <Sites
            sites={summary.sites}
            selected={query.siteId}
            onSelect={(siteId) =>
              setQuery((q) => ({ ...q, siteId: q.siteId === siteId ? undefined : siteId }))
            }
          />
        </>
      )}

      {/* ------------------------------------------------------------------ */}

      <section className="space-y-3">
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Worklist">
          {SEGMENTS.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={segment === value}
              onClick={() => setQuery((q) => ({ ...q, segment: value }))}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm font-medium",
                segment === value
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {SegmentTitles[value]}
            </button>
          ))}
        </div>

        {/*
          ⚠️ Says what each list is FOR, not just what it is called. "Dormant" and "Never signed
          in" sound like two views of one number and are two different problems with two different
          fixes — one account was issued and never taken up, the other was taken up and abandoned.
        */}
        <p className="max-w-3xl text-sm text-muted-foreground">{SegmentDescriptions[segment]}</p>
      </section>

      <section className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3">
        <form onSubmit={submitSearch} className="flex min-w-[16rem] flex-1 items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Search name, email or job title"
              aria-label="Search name, email or job title"
              className="h-9 w-full rounded-lg border border-border bg-background pl-8 pr-3 text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
            />
          </div>

          {/*
            ⚠️ A real submit button, not just Enter. The search runs on submit rather than per
            keystroke, and a box that only responds to a key nobody was told to press reads as
            broken — people type, wait, and conclude the search does not work.
          */}
          <button
            type="submit"
            className="h-9 shrink-0 rounded-lg border border-border px-3 text-sm font-medium hover:bg-muted"
          >
            Search
          </button>
        </form>

        {/*
          ⚠️ The window is offered whatever tab is open, and it deliberately does nothing on the
          never-signed-in list — those accounts have no last sign-in for a threshold to apply to.
          Hiding it would move the control around as the reader changes tabs; disabling it says
          what it does.
        */}
        <Field label="Idle for at least">
          <select
            value={query.window ?? "Days90"}
            disabled={segment === "NeverSignedIn"}
            onChange={(event) =>
              setQuery((q) => ({ ...q, window: event.target.value as DormancyWindow }))
            }
            className="h-9 rounded-lg border border-border bg-background px-2 text-sm disabled:opacity-50"
          >
            {WINDOWS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        {filtered && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear filters
          </button>
        )}

        <Counts
          matching={totalMatching}
          inSegment={totalInSegment}
          accounts={totalAccounts}
          filtered={filtered}
          includeInactive={query.includeInactive ?? false}
        />
      </section>

      {error && (
        <p className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
          {error}
        </p>
      )}

      {loading ? (
        <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading…
        </p>
      ) : rows.length === 0 ? (
        <Empty segment={segment} filtered={filtered} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full min-w-[52rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <SortableHeader
                    label="Name"
                    field="Name"
                    active={query.sortBy}
                    direction={query.direction}
                    onSort={sortBy}
                    className="pl-4"
                  />
                  <SortableHeader
                    label="Job title"
                    field="JobTitle"
                    active={query.sortBy}
                    direction={query.direction}
                    onSort={sortBy}
                  />
                  <th scope="col" className="py-2.5 pr-3 font-medium">
                    Site
                  </th>
                  <th scope="col" className="py-2.5 pr-3 font-medium">
                    Manager
                  </th>
                  {/*
                    ⚠️ "Last signed in", and it is a DATE. The server has nowhere to put a time —
                    see AppUsagePersonResponse. A worklist needs to know an account has been idle
                    for months; a time of day would make this a record of when a named colleague
                    is at their desk (D-56, D-118).
                  */}
                  <th scope="col" className="py-2.5 pr-4 font-medium">
                    Last signed in
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map((person) => (
                  <PersonRow key={person.id} person={person} linked={canOpenProfiles} />
                ))}
              </tbody>
            </table>
          </div>

          {cursor && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-60"
              >
                {loadingMore ? "Loading…" : "Show more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * This month against last month.
 *
 * ⚠️ **The absent comparison is the important state, not the edge case.** `LastSeenAtUtc` is
 * overwritten on every request, so until the monthly rollup has a whole month behind it there is
 * no record of who used the desk in any month but this one. Drawing a 0 would tell somebody their
 * organisation abandoned the service desk last month.
 */
function MonthlyCard({ summary }: { summary: AppUsageSummary }) {
  const { thisMonth, lastMonth, changeFromLastMonth, coverageFromYearMonth } = summary.monthly;

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">People using the desk this month</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums">{thisMonth.toLocaleString()}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            of {summary.accounts.total.toLocaleString()}{" "}
            {summary.includesInactiveAccounts ? "accounts" : "active accounts"}
          </p>
        </div>

        {lastMonth === null ? (
          /*
            ⚠️ Says WHY there is no comparison rather than leaving a blank the reader fills in
            themselves. "No data" reads as a fault; this reads as a start date, which is what it
            is — and it tells them when it will start working.
          */
          <p className="max-w-sm rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            No comparison yet. Monthly usage has only been recorded since{" "}
            {coverageFromYearMonth ? formatYearMonth(coverageFromYearMonth) : "this month"}, and
            before that the system kept only each person&rsquo;s most recent sign-in — which cannot
            say who was active in an earlier month. A month-on-month figure appears once a full
            month has been measured.
          </p>
        ) : (
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Last month</p>
            <p className="mt-1 text-xl font-semibold tabular-nums">{lastMonth.toLocaleString()}</p>
            {changeFromLastMonth !== null && (
              /*
                ⚠️ A difference in ACCOUNTS, never a percentage (D-58). And not coloured as good
                or bad: a quiet month at a hospital is not obviously a failure, and a busy one is
                not obviously a success.
              */
              <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                {changeFromLastMonth > 0 ? "+" : ""}
                {changeFromLastMonth.toLocaleString()}{" "}
                {Math.abs(changeFromLastMonth) === 1 ? "account" : "accounts"}
              </p>
            )}
          </div>
        )}
      </div>

      <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
        {summary.monthly.newAccountsThisMonth.toLocaleString()} new{" "}
        {summary.monthly.newAccountsThisMonth === 1 ? "account" : "accounts"} this month
        {summary.monthly.newAccountsLastMonth !== null && (
          <> · {summary.monthly.newAccountsLastMonth.toLocaleString()} last month</>
        )}
        . An account is created when somebody first signs in, or when the directory sync or a
        colleague adds them.
      </p>
    </section>
  );
}

function AdoptionTiles({ summary }: { summary: AppUsageSummary }) {
  const { accounts, adoption } = summary;

  const share = accounts.total > 0 ? Math.round((adoption.everSignedIn / accounts.total) * 100) : 0;

  return (
    <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        label="Have signed in"
        value={adoption.everSignedIn}
        hint={`${share}% of ${accounts.total.toLocaleString()}`}
      />
      <Tile
        label="Never signed in"
        value={adoption.neverSignedIn}
        hint="Accounts that have never been used"
      />
      <Tile label="Active in 30 days" value={adoption.activeLast30Days} hint="Signed in recently" />
      <Tile label="Active in 7 days" value={adoption.activeLast7Days} hint="Signed in this week" />
    </section>
  );
}

/**
 * Administrative access nobody is behind.
 *
 * ⚠️ The one card here that is a security control rather than an adoption measure. An account
 * holding break-glass, audit or export permissions that nobody has signed into for months is
 * standing access to a PHI-bearing system with no one behind it — §164.308(a)(4).
 */
function AdministratorCard({ summary, onOpen }: { summary: AppUsageSummary; onOpen: () => void }) {
  const { total, dormant, neverSignedIn } = summary.administrators;

  const clean = dormant === 0;

  return (
    <section
      className={cn(
        "rounded-xl border p-4",
        clean ? "border-border bg-card" : "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <ShieldAlert
            className={cn(
              "mt-0.5 h-5 w-5 shrink-0",
              clean ? "text-muted-foreground" : "text-amber-600",
            )}
            aria-hidden
          />
          <div>
            <h2 className="group/card flex items-center gap-1 font-semibold">
              Administrative accounts
              <InfoTip id="usage-admins" />
            </h2>
            <p className="mt-0.5 max-w-2xl text-sm text-muted-foreground">
              {clean ? (
                <>
                  All {total.toLocaleString()} administrative{" "}
                  {total === 1 ? "account has" : "accounts have"} been used within the last{" "}
                  {summary.dormancyWindowDays} days.
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">
                    {dormant.toLocaleString()} of {total.toLocaleString()}
                  </span>{" "}
                  administrative accounts have not been used in {summary.dormancyWindowDays} days
                  {neverSignedIn > 0 && <> — {neverSignedIn.toLocaleString()} never at all</>}.
                  Standing access with nobody behind it is worth reviewing.
                </>
              )}
            </p>
          </div>
        </div>

        {/*
          ⚠️ D-70's rule: a figure must open the rows it counted. A card saying "4 accounts" with
          no way to see which four is a number somebody has to go and re-derive by hand.
        */}
        {!clean && (
          <button
            type="button"
            onClick={onOpen}
            className="shrink-0 rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Review them
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Monthly actives.
 *
 * ⚠️ A bar per month rather than a line, because a line implies the points between are
 * measurements and there is nothing between two monthly buckets. Partial months are hatched and
 * labelled at both ends — the first because measurement started part-way through it, the last
 * because it has not finished.
 */
function Trend({ points, coverage }: { points: AppUsageMonthPoint[]; coverage: number | null }) {
  if (points.length === 0) return null;

  const peak = Math.max(...points.map((p) => p.activeAccounts), 1);

  return (
    <section className="group/card rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-1 font-semibold">
          Monthly usage
          <InfoTip id="usage-monthly" />
        </h2>
        <p className="text-xs text-muted-foreground">
          {coverage
            ? `Recorded since ${formatYearMonth(coverage)}`
            : "Recording started this month"}
        </p>
      </div>

      {points.length === 1 && (
        <p className="mt-2 text-sm text-muted-foreground">
          One month so far. There is nothing to compare against until next month.
        </p>
      )}

      <div className="mt-4 flex items-end gap-2 overflow-x-auto pb-1">
        {points.map((point) => (
          <div
            key={point.yearMonth}
            className="flex min-w-[3rem] flex-1 flex-col items-center gap-1.5"
          >
            <span className="text-xs font-medium tabular-nums">
              {point.activeAccounts.toLocaleString()}
            </span>
            <div
              className={cn(
                "w-full rounded-t",
                point.isPartial ? "bg-primary/30" : "bg-primary/70",
              )}
              style={{ height: `${Math.max((point.activeAccounts / peak) * 96, 2)}px` }}
              role="img"
              aria-label={`${formatYearMonth(point.yearMonth)}: ${point.activeAccounts} accounts${
                point.isPartial ? ", partial month" : ""
              }`}
            />
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              {formatYearMonth(point.yearMonth)}
            </span>
            {point.isPartial && (
              <span className="text-[10px] text-muted-foreground">part month</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Take-up per site.
 *
 * ⚠️ Every account is in exactly one row, including the ones placed nowhere (D-54) — the
 * breakdown sums to the headline or it is not a breakdown. The "No site" row is the population
 * the users-without-a-site report exists for, and it is clickable here for the same reason.
 */
function Sites({
  sites,
  selected,
  onSelect,
}: {
  sites: AppUsageSitePoint[];
  selected: string | undefined;
  onSelect: (siteId: string) => void;
}) {
  if (sites.length === 0) return null;

  return (
    <section className="group/card rounded-xl border border-border bg-card">
      <h2 className="flex items-center gap-1 border-b border-border px-4 py-3 font-semibold">
        Take-up by site
        <InfoTip id="usage-site" />
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th scope="col" className="py-2 pl-4 font-medium">
                Site
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                Accounts
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                Signed in
              </th>
              <th scope="col" className="py-2 pr-3 text-right font-medium">
                Active 30d
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Take-up
              </th>
            </tr>
          </thead>

          <tbody>
            {sites.map((site) => {
              const token = site.siteId ?? UnsitedToken;
              const share = site.accounts > 0 ? (site.everSignedIn / site.accounts) * 100 : 0;

              return (
                <tr
                  key={token}
                  className={cn(
                    "border-b border-border/60 last:border-0",
                    selected === token && "bg-primary/5",
                  )}
                >
                  <td className="py-2 pl-4">
                    <button
                      type="button"
                      onClick={() => onSelect(token)}
                      className="text-left hover:underline"
                    >
                      {site.siteName}
                    </button>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {site.accounts.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {site.everSignedIn.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                    {site.activeLast30Days.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary/70"
                          style={{ width: `${share}%` }}
                        />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {Math.round(share)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Tile({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/**
 * The figures, with their denominators.
 *
 * ⚠️ D-53's rule: 38 unused accounts is alarming out of 40 and unremarkable out of 2,600. And
 * D-58's: a filter must narrow AND SAY SO.
 */
function Counts({
  matching,
  inSegment,
  accounts,
  filtered,
  includeInactive,
}: {
  matching: number | null;
  inSegment: number | null;
  accounts: number | null;
  filtered: boolean;
  includeInactive: boolean;
}) {
  if (matching === null || inSegment === null || accounts === null) return null;

  const noun = includeInactive ? "accounts" : "active accounts";

  return (
    <p className="w-full text-sm">
      <span className="text-lg font-semibold tabular-nums">{matching.toLocaleString()}</span>
      <span className="text-muted-foreground">
        {filtered ? (
          <>
            {" "}
            of {inSegment.toLocaleString()} on this list, out of {accounts.toLocaleString()} {noun}
          </>
        ) : (
          <>
            {" "}
            of {accounts.toLocaleString()} {noun}
          </>
        )}
      </span>
    </p>
  );
}

function Empty({ segment, filtered }: { segment: AppUsageSegment; filtered: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      {filtered ? (
        <p className="text-sm text-muted-foreground">
          Nobody on this list matches those filters. Clearing them shows the whole list.
        </p>
      ) : (
        <div className="space-y-1">
          <p className="text-sm font-medium">Nothing to do here.</p>
          <p className="text-sm text-muted-foreground">
            {segment === "NeverSignedIn"
              ? "Every account has been signed into at least once."
              : segment === "Dormant"
                ? "Every account that has been used is still being used."
                : "Every administrative account has been used recently."}
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function SortableHeader({
  label,
  field,
  active,
  direction,
  onSort,
  className,
}: {
  label: string;
  field: AppUsageSortField;
  active: AppUsageSortField | undefined;
  direction: "Ascending" | "Descending" | undefined;
  onSort: (field: AppUsageSortField) => void;
  className?: string;
}) {
  const isActive = active === field;

  return (
    <th
      scope="col"
      className={cn("py-2.5 pr-3 font-medium", className)}
      aria-sort={isActive ? (direction === "Ascending" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-foreground"
      >
        {label}
        <ArrowUpDown
          className={cn("h-3 w-3", isActive ? "opacity-100" : "opacity-40")}
          aria-hidden
        />
      </button>
    </th>
  );
}

function PersonRow({ person, linked }: { person: AppUsagePerson; linked: boolean }) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="py-2.5 pl-4 pr-3">
        <div className="flex items-center gap-2">
          <Avatar
            userId={person.id}
            name={person.displayName}
            hasPicture={person.hasAvatar}
            size="sm"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <PersonLink id={person.id} name={person.displayName} linked={linked} />

              {person.isAdministrator && (
                /*
                  ⚠️ Badged on every list, not only the administrators one. It is the single fact
                  that changes what a reader should do about a dormant account: closing an unused
                  requester account is housekeeping, closing an unused account that can read the
                  audit log is a control.
                */
                <Tag tone="amber">Administrator</Tag>
              )}
              {!person.isActive && <Tag>Deactivated</Tag>}
              {person.isExternalGuest && <Tag>Guest</Tag>}
            </div>
            {person.email && (
              <p className="truncate text-xs text-muted-foreground">{person.email}</p>
            )}
          </div>
        </div>
      </td>

      <td className="py-2.5 pr-3 text-muted-foreground">{person.jobTitle ?? "—"}</td>

      <td className="py-2.5 pr-3 text-muted-foreground">
        {person.siteName ?? <span className="text-muted-foreground/70">No site</span>}
      </td>

      <td className="py-2.5 pr-3 text-muted-foreground">
        {person.managerUserId && person.managerName ? (
          <PersonLink id={person.managerUserId} name={person.managerName} linked={linked} />
        ) : (
          <span className="inline-flex items-center gap-1 text-muted-foreground/70">
            <AlertTriangle className="h-3 w-3" aria-hidden />
            Nobody to ask
          </span>
        )}
      </td>

      <td className="py-2.5 pr-4">
        {person.lastSignedInOn === null ? (
          <span className="text-muted-foreground/70">Never</span>
        ) : (
          <span className="whitespace-nowrap">
            {formatSignInDate(person.lastSignedInOn)}
            {person.daysSinceLastSignIn !== null && (
              <span className="ml-1.5 text-xs tabular-nums text-muted-foreground">
                {person.daysSinceLastSignIn}d
              </span>
            )}
          </span>
        )}
      </td>
    </tr>
  );
}

function PersonLink({ id, name, linked }: { id: string; name: string; linked: boolean }) {
  if (!linked) return <span className="font-medium">{name}</span>;

  return (
    <Link to="/people/$userId" params={{ userId: id }} className="font-medium hover:underline">
      {name}
    </Link>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: "amber" }) {
  return (
    <span
      className={cn(
        "rounded border px-1.5 py-0.5 text-[11px] font-medium",
        tone === "amber"
          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "border-border bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function ExportButton({
  disabled,
  busy,
  rowCount,
  segment,
  filtered,
  onExport,
}: {
  disabled: boolean;
  busy: boolean;
  rowCount: number;
  segment: AppUsageSegment;
  filtered: boolean;
  onExport: (justification: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [justification, setJustification] = useState("");

  const tooShort = justification.trim().length < MinimumJustificationLength;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (tooShort) return;

    try {
      await onExport(justification.trim());
      setOpen(false);
      setJustification("");
    } catch {
      // Reported on the page behind the dialog, which stays open so the typed reason is not lost.
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium hover:bg-muted disabled:opacity-50"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        Export CSV
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={submit}
            className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-5 shadow-lg"
          >
            <div>
              <h2 className="font-semibold">Export {rowCount.toLocaleString()} accounts</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {SegmentTitles[segment]}
                {filtered && " — the current filters are applied, and are recorded in the file."}
              </p>
            </div>

            {/*
              ⚠️ Says what the file contains and what happens when it is produced. "Personal data"
              rather than "PHI", because that is what this is — overstating it would teach people
              to discount the warning on exports that really do carry clinical detail.
            */}
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              The file contains names, email addresses, reporting lines and the date each account
              was last signed into. It is recorded against your account as a high-severity export
              and carries a watermark identifying you.
            </p>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Why is this export needed?</span>
              <textarea
                value={justification}
                onChange={(event) => setJustification(event.target.value)}
                rows={3}
                autoFocus
                className="w-full rounded-lg border border-border bg-background p-2 text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
                placeholder="e.g. Quarterly review of administrative access"
              />
              <span className="text-xs text-muted-foreground">
                At least {MinimumJustificationLength} characters.
              </span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={tooShort || busy}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy ? "Preparing…" : "Export"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
